import { EmptyState } from 'construct-ui';
import Flexmonster from 'flexmonster';
import { REPORT_GRID_LAYOUT } from "../../config/constants";
import { FLEXMONSTER_LICENSE } from '../../config/global-variables';
import ReportModel from '../../models/report/report-model.model';
import NonNullableObjectProxy from '../../utils/NonNullableObjectProxy';
import ViewBase from '../../views/view-base.view';

export default function PivotTableComponent() {
  ViewBase.call(this);

  const REGEX_LABEL_NAME = /[^{}]+/;

  /** Supported Format
   * {(Currency Symbol)(Thousands Separator).(Decimals or #)(Percentage Symbol)(Currency Symbol)}
   * {$,.0#%SGD}
   * {$}
   * {.00}
   * {%}
   */
  const REGEX_LABEL_FORMAT = /(?<={)([^\d\s,.%]*)([, ]?)\.?([\d#]*?)(%?)([^\d\s,.%]*?)(?=})/;

  // Find variable names or start with DCOUNT::
  const REGEX_ZAXIS_VARIABLES = /\b[A-Za-z_]+\b(?!::)|DCOUNT::\b[A-Za-z_]+\b|S::\b[A-Za-z_]+\b/g;

  const COMPONENT_ID = `pivot-table-component-${uniqueInstance++}`;
  const DEFAULT_SHOW_FILTER = () => { };
  const DEFAULT_GRID_TYPE = 'classic';
  const DEFAULT_HEIGHT = 250;
  const DEFAULT_TABS = [
    // 'Connect',
    // 'Open',
    // 'Save',
    'Export',
    // 'Share',
    'Grid',
    'Charts',
    'Format',
    'Options',
    'Fields',
    'Fullscreen'
  ];

  let _pivotConfig = { rows: [], cols: [], vals: [] };
  let _rawParam = {};
  let _reportParameter = {};
  // let _reportDetailParameter = {};
  let customCellFormat;
  let lastGenerated;
  // let gridType;
  let pivot;
  let pivotData;
  let pivotReport;
  let cellClickEvent;
  let reportID;
  let version = '';

  let reportParams = () => { };
  
  const setParams = () => {
    _rawParam = reportParams();
    customCellFormat = _rawParam.customCellFormat; // Array of custom cell formats

    _reportParameter = {};
    const proxy = NonNullableObjectProxy(_reportParameter);

    proxy.dataSourceCode = _rawParam.dataSourceCode;      // "McsProProduct"
    proxy.periodCode = _rawParam.periodCode;              // "M",
    proxy.fromDate = _rawParam.fromDate;                  // "202201",
    proxy.toDate = _rawParam.toDate;                      // "202203",
    proxy.xAxis = _rawParam.xAxis?.join('^^') || '';      // "Year^^Month",
    proxy.yAxis = _rawParam.yAxis?.join('^^') || '';      // "BU^^Div",
    proxy.zAxis = _rawParam.zAxis?.join('^^') || '';      // "SO Qty:=SELLOUT_QTY^^SO Amt:=SELLOUT_AMT"
    proxy.parameters = _rawParam.parameters;
    proxy.fromCompareDate = _rawParam.fromCompareDate;    // "202201",
    proxy.toCompareDate = _rawParam.toCompareDate;        // "202203"
    proxy.id = _rawParam.id ? _rawParam.id : null;      // mobile report template id
    proxy.filter = _rawParam.filter ? _rawParam.filter : null; // mobile report template filter
  }

  const getCustomFormats = (zAxis) => {
    let formats = {};
    const labels = zAxis.split('^^').map(label => label.split(':=')[0]);

    labels.forEach(label => {
      const format = parseNumberFormat(label);
      if (format) {
        formats[format.name] = format;
      }
    });

    return Object.values(formats);
  }

  const parseNumberFormat = (label) => {
    const formatParts = label.match(REGEX_LABEL_FORMAT);
    if (!formatParts) return null;

    const [formatString, preCurrencySymbol, thousandsSeparator, decimals, percentage, postCurrencySymbol] = formatParts;

    const format = {
      name: formatString,
      decimalSeparator: '.',
      positiveCurrencyFormat: '$1',
      negativeCurrencyFormat: '-$1'
    };

    if (thousandsSeparator) {
      format.thousandsSeparator = thousandsSeparator;
    }

    if (percentage) {
      format.isPercent = true;
    }

    if (decimals) {
      format.decimalPlaces = decimals.length;
    }

    if (preCurrencySymbol) {
      format.currencySymbol = preCurrencySymbol;
    }

    if (postCurrencySymbol) {
      format.currencySymbol = postCurrencySymbol;
      format.positiveCurrencyFormat = '1$';
      format.negativeCurrencyFormat = '-1$';
    }

    return format;
  }

  const getFieldTypeMappings = (rawParam) => {
    let mapping = {};

    rawParam.xAxis.forEach(x => {
      mapping[x.split(':=')[0]] = { type: 'string' };
    })

    rawParam.yAxis.forEach(y => {
      mapping[y.split(':=')[0]] = { type: 'string' };
    })

    rawParam.zAxis.forEach(z => {
      const FORMULA_INDEX = 1;
      const FORMULA_DCOUNT = 'DCOUNT::';
      const FORMULA_STRING = 'S::';

      const formula = z.split(':=')[FORMULA_INDEX];
      let variables = formula.match(REGEX_ZAXIS_VARIABLES);
      if (variables && Array.isArray(variables)) {
        variables.forEach(variable => {
          if (variable.startsWith(FORMULA_DCOUNT)) {
            let variableName = variable.replace(FORMULA_DCOUNT, '');
            mapping[variableName] = { type: 'string' };
          } else if (variable.startsWith(FORMULA_STRING)) {
            let variableName = variable.replace(FORMULA_STRING, '');
            mapping[variableName] = { type: 'string' };
          } else {
            mapping[variable] = { type: 'number' };
          }
        })
      }
    })
    return mapping;
  }

  const setPivotConfig = () => {
    _pivotConfig.rows = _getRows();
    _pivotConfig.cols = _getColumns();
    _pivotConfig.vals = _getValues();
  }

  const clearPivotTable = () => {
    pivotData = undefined;
    pivot.clear();
  }

  const getReportData = () => {
    this.showLoading();

    setParams();
    setPivotConfig();

    m.redraw();    
    ReportModel.getReportData(_reportParameter, version)
      .then((result) => {
        if (!result || result.length === 0) {
          clearPivotTable();
          return;
        }

        pivotData = result;
        const customFormats = getCustomFormats(_reportParameter.zAxis);
        const fieldTypeMappings = getFieldTypeMappings(_rawParam);

        pivotReport = {
          formats: [
            {
              name: "",
              thousandsSeparator: ",",
              decimalSeparator: "",
              decimalPlaces: 0,
              isPercent: false,
              divideByZeroValue: '0'
            },
            {
              name: "percent",
              thousandsSeparator: ",",
              decimalSeparator: ".",
              decimalPlaces: 2,
              isPercent: true
            },
            {
              name: "amount",
              thousandsSeparator: ",",
              decimalSeparator: ".",
              decimalPlaces: 2,
              isPercent: false
            },
            ...customFormats
          ],
          dataSource: {
            dataSourceType: "json",
            data: pivotData,
            mapping: fieldTypeMappings
          },
          options: {
            configuratorButton: false,
            grid: {
              type: reportParams().layout ?? DEFAULT_GRID_TYPE//classic, flat, compact
            },
            drillThrough: false,
          },
          slice: {
            rows: _pivotConfig.rows,
            columns: _pivotConfig.cols,
            measures: _pivotConfig.vals
          }
        }
        pivot.setReport(pivotReport);
        pivot.expandAllData();
        if (reportID === 'mobile-report-pivot') {
          setCellClick();
        }
      })
      .catch((error) => {
        clearPivotTable();
        this.handleError(error);
      })
      .finally(() => {
        this.hideLoading();
        m.redraw();
      })
  }

  // X-Axis
  const _getRows = () => {
    let rows = []
    _rawParam.yAxis.forEach((y) => {
      rows.push({ uniqueName: y });
    })
    return rows;
  }

  // Y-Axis
  const _getColumns = () => {
    let cols = []
    _rawParam.xAxis.forEach((x) => {
      cols.push({ uniqueName: x });
    })
    cols.push({ uniqueName: "[Measures]" });
    return cols;
  }

  // Z-Axis
  const _getValueType = (val) => {
    let format = val.match(REGEX_LABEL_FORMAT)?.at(0);

    if (format) {
      return format;
    }
    else if (val.includes('%')) {
      return 'percent';
    }
    else if (val.includes('##') || val.includes('Amt') || val.includes('Amount')) {
      return 'amount';
    }

    return ''
  }

  const _getValues = () => {
    let vals = [];
    _rawParam.zAxis.forEach((z) => {
      const zz = z.split(':=');
      let label = zz[0]
      let formula = zz.length > 1 ? zz[1] : zz[0];

      let _measure = {
        uniqueName: label.match(REGEX_LABEL_NAME)?.at(0)?.trim(),
        formula: _buildFormula(formula),
        format: _getValueType(label)
      };

      vals.push(_measure);
    })
    return vals;
  }

  const _buildFormula = (formula) => {
    const regex = /[(),<>=+\-*\/]|[\w:]+/g;///[^\+\-\*\/\(\)]+/g;
    const matches = formula.match(regex);
    const values = Array.from(matches);
    let newValues = [];
    for (var i = 0; i < values.length; i++) {
      let val = values[i];
      if (['(', ')', ',', '<', '>', '=', '+', '-', '*', '/'].includes(val)) {
        newValues.push(val);
        continue;
      }
      let wAggr = _setPivotFormula(val);
      newValues.push(wAggr);
    }
    return newValues.join('');
  }

  const _setPivotFormulaTotal = (val, type, isDistinct, isFormula = false) => {
    let aggr = '';
    switch (type) {
      case 'Y::':
        aggr = 'percentOfColumn';
        break;
      case 'YS::':
        aggr = 'percentOfParentRowTotal';
        break;
      case 'X::':
        aggr = 'percentOfRow';
        break;
      case 'XS::':
        aggr = 'percentOfParentColumnTotal';
        break;
      default:
        ''
    }

    let newVal = isFormula ? val : `"${val}"`; // Wrap in quotation marks if it is not a formula
    newVal = newVal.replace(type, '');
    let res = '((' + (isDistinct ? 'distinctcount' : 'sum') + '(' + newVal + ') / ' + aggr + '(' + newVal + ')) * 100)';
    return res;
  }

  const _setPivotFormula = (val) => {
    let newVal = val;
    let isFormula = false;
    let isDistinct = false;
    let tmpVal = '';
    if (newVal.includes('DCOUNT::')) { // Distinct Count
      tmpVal = newVal.replace('DCOUNT::', '');
      newVal = 'distinctcount(\"' + tmpVal + '\")';
      isDistinct = true;
      isFormula = true;
    }
    if (newVal.includes('MAX::')) { // MAX
      tmpVal = newVal.replace('MAX::', '');
      newVal = 'max(\"' + tmpVal + '\")';
      isFormula = true;
    }
    if (newVal.includes('MIN::')) { // MIN
      tmpVal = newVal.replace('MIN::', '');
      newVal = 'min(\"' + tmpVal + '\")';
      isFormula = true;
    }
    if (newVal.includes('Y::')) { // Y Total
      newVal = _setPivotFormulaTotal(newVal, 'Y::', isDistinct, isFormula);
      isFormula = true;
    }
    if (newVal.includes('YS::')) { // Y Sub Total
      newVal = _setPivotFormulaTotal(newVal, 'YS::', isDistinct, isFormula);
      isFormula = true;
    }
    if (newVal.includes('X::')) { // X Total
      newVal = _setPivotFormulaTotal(newVal, 'X::', isDistinct, isFormula);
      isFormula = true;
    }
    if (newVal.includes('XS::')) { // X Sub Total
      newVal = _setPivotFormulaTotal(newVal, 'XS::', isDistinct, isFormula);
      isFormula = true;
    }
    if (newVal.includes('IF::')) { // IF
      newVal = newVal.replace('IF::', 'if');
      isFormula = true;
    }
    if (newVal.includes('S::')) { // String
      newVal = newVal.replace('S::', '');
      isFormula = true;
    }
    // if (newVal.includes('F::')) { // File or Link
    //   newVal = newVal;
    //   isFormula = true;
    // }
    if (!isFormula) { // Default - SUM
      newVal = 'sum(\"' + newVal + '\")';
    }
    return newVal;
  }

  const cleanString = (str) => {
    let clean = str;
    if (str.indexOf('::') !== -1) {
      clean = str.substring(str.indexOf('::') + 2, str.length);
    }
    return clean;
  }

  const customizeCellFunction = (cell, data) => {
    let highlightedMembers = [];
    if (Array.isArray(_rawParam.linkYAxis) && Array.isArray(_rawParam.linkZAxis)) {
      highlightedMembers = _rawParam.linkYAxis.concat(_rawParam.linkZAxis).map((x) => x.name)
    }
    if (data.isClassicTotalRow) {
      cell.addClass("fm-total-classic-r");
    }
    if (reportID == "mobile-report-pivot" && data.type === "value" && data.measure?.uniqueName && highlightedMembers.includes(data.measure?.uniqueName)) {
      cell.addClass("highlight")
    }
    if (data.type === "value" && data.measure?.uniqueName && hasMatchingIFFormat(data.measure.uniqueName)) {
      const ifFormat = customCellFormat.find(f => f.type?.toUpperCase() === 'IF' && f.format?.label === data.measure.uniqueName);
      const formattedValue = ifFormat.format.values.find(v => v.code === data.value.toString());

      if (formattedValue) {
        cell.text = formattedValue.name;
      }
    }

    cell.text = cleanString(cell.text);
  }

  const _getSelectedFilters = (cell) => {
    switch (_rawParam.layout) {
      case REPORT_GRID_LAYOUT.CLASSIC:
      case REPORT_GRID_LAYOUT.COMPACT: {
        const _rows = cell.rows.map((x, i) => { return { name: x.hierarchyName, value: x.caption }; })
        const _columns = cell.columns.map((x, i) => { return { name: x.hierarchyName, value: x.caption }; })
        return _rows.concat(_columns);
      }
      case REPORT_GRID_LAYOUT.FLAT: {
        const measures = pivot.getMeasures();
        if (measures.length > 0) {
          const arr = measures.map((x, i) => {
            return { name: x.uniqueName, value: pivot.getCell(cell.rowIndex, i).label };
          });
          return arr;
        }
        break;
      }
      default: {
        return [];
      }
    }
  }

  const _getSelectedData = (cell) => {
    let _selectedDataTitle;
    switch (_rawParam.layout) {
      case REPORT_GRID_LAYOUT.CLASSIC: {
        if (cell.type == 'header' && cell.member != null && !cell.isTotal) {
          _selectedDataTitle = cell.hierarchy.uniqueName;
        } else if (cell.type == 'value' && cell.measure != null && !cell.isTotal) {
          _selectedDataTitle = cell.measure.uniqueName;
        }
        break;
      }
      case REPORT_GRID_LAYOUT.FLAT: {
        _selectedDataTitle = cell.measure?.uniqueName;
        break;
      }
      case REPORT_GRID_LAYOUT.COMPACT: {
        if (cell.type == 'header' && cell.member != null) {
          _selectedDataTitle = cell.hierarchy.uniqueName;
        } else if (cell.type == 'value' && cell.measure != null && !cell.isTotal) {
          _selectedDataTitle = cell.measure.uniqueName;
        }
        break;
      }
      default: {
        _selectedDataTitle = '';
        break;
      }
    }
    let _selected
    if (_selectedDataTitle) {
      _selected = _rawParam.linkZAxis.find(xItem => xItem.name === _selectedDataTitle) || _rawParam.linkYAxis.find(yItem => yItem.name === _selectedDataTitle);
      if (_selected) {
        _selected.value = cell.label;
      }
    }
    return _selected;
  }

  const getChildReportDetails = (cell) => {
    const childReportDetails = {
      detailData: {},              // Column headers
      detailFilter: [],              // Row headers
    };
    const _selected = _getSelectedData(cell);
    if (_selected) {
      const _selectedFilters = _getSelectedFilters(cell);
      childReportDetails.detailData = _selected;
      childReportDetails.detailFilter = _selectedFilters;
    }
    return childReportDetails;
  }

  const setCellClick = () => {
    if ((Array.isArray(_rawParam.linkYAxis) && _rawParam.linkYAxis.length > 0) || (Array.isArray(_rawParam.linkZAxis) && _rawParam.linkZAxis.length > 0)) {
      pivot.on('cellclick', function (cell) {
        const linkArray = _rawParam.linkYAxis.concat(_rawParam.linkZAxis);
        const linkElementName = linkArray.find((ele) => ele.name === cell.measure?.uniqueName);

        if (!cell || !cell.label || cell.label === '(blank)') {
          return;
        } else if (cell.type === 'header') {
          return;
        } else if (linkElementName === undefined) {
          return;
        } else {
          const childReportDetailsData = getChildReportDetails(cell);
          if (cellClickEvent) cellClickEvent(linkElementName, childReportDetailsData);
        }
      });
    }
  }

  const hasMatchingIFFormat = (label) => {
    return !!customCellFormat?.some(f => f.type?.toUpperCase() === 'IF' && f.format?.label === label);
  }

  const initializeFlexmonster = (componentId, defaultTabs) => {
    pivot = new Flexmonster({
      licenseKey: FLEXMONSTER_LICENSE,
      container: componentId,
      componentFolder: "node_modules/flexmonster/",
      customizeCell: customizeCellFunction,
      height: "100%",
      toolbar: defaultTabs.length > 0 ? true : false,
      beforetoolbarcreated: (toolbar) => {
        // get all tabs         
        let tabs = toolbar.getTabs();
        toolbar.getTabs = function () {
          // remove the Connect tab using its id
          // tabs = tabs.filter(tab => tab.id != "fm-tab-connect" && tab.id != "fm-tab-open" && tab.id != "fm-tab-save");
          tabs = tabs.filter(t => defaultTabs.includes(t.title));//tabs.filter(tab => defaultTabs.. tab.title");
          return tabs;
        }
      }
    });
  }

  return {
    oninit: (vnode) => {
      reportParams = vnode.attrs?.reportParams;
      reportID = vnode.attrs?.id;      
      if (vnode.attrs?.onCellClick) {
        cellClickEvent = vnode.attrs.onCellClick;
      }

    },
    oncreate: ({ attrs: { id = COMPONENT_ID, defaultTabs = DEFAULT_TABS } }) => {
      initializeFlexmonster(id, defaultTabs);
    },
    onupdate: (vnode) => {
      if (lastGenerated === vnode.attrs.lastGenerated) { return };
      lastGenerated = vnode.attrs.lastGenerated;
      version = vnode.attrs.version;
      reportParams = vnode.attrs.reportParams;
      if (lastGenerated) {
        // gridType = reportParams.layout ?? DEFAULT_GRID_TYPE;
        getReportData();
      } else {
        clearPivotTable();
      }
    },
    view: ({ attrs: { id = COMPONENT_ID, showFilter = DEFAULT_SHOW_FILTER, reportHeight = DEFAULT_HEIGHT } }) => {
      return m('.pivot-component-container', { style: `height: calc(100vh - ${reportHeight}px); position: relative;` },
        !pivotData &&
        m(EmptyState, {
          content: m('.d-flex.flex-column.align-items-center.justify-content-center.gap-2', {
            style: {
              'width': '300px',
              'height': '200px',
              'background-color': 'var(--off-white-color)',
              'border-radius': '0.375rem',
            },
          },
            m('.fs-6.font-weight-bold', this.getText("LT00000211", "No records")),
            m('button.btn.btn-primary',
              { onclick: showFilter },
              this.getText("LT00001004", "Go to Report Filter")
            )
          ),
          fill: true,
          style: { 'background-color': '#7070708a' },
        }),
        m('div', { id }),
        this.getBaseViews()
      )
    },
  }
}

let uniqueInstance = 1;
